import React, { useState, useEffect } from 'react';
import { CopyAnalysisResult } from '../types/copyAnalysis';
import { ResizableContainer } from './ResizableContainer';
import { LoginPopup } from './LoginPopup';
import { useScrollLock } from '../hooks/useScrollLock';
import { AnalysisHeader } from './analysis/AnalysisHeader';
import { IssueList } from './analysis/IssueList';
import { ImagePreview } from './analysis/ImagePreview';
import { useAuthContext } from '@/contexts/AuthContext';

interface CopyAnalysisTableProps {
  analysis: CopyAnalysisResult | null;
  screenshot?: string;
}

export function CopyAnalysisTable({ analysis, screenshot }: CopyAnalysisTableProps) {
  const { user } = useAuthContext();
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const [showAllIssues, setShowAllIssues] = useState(false);

  useScrollLock(showLoginPopup);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300 && !showAllIssues && !user) {
        setIsBlurred(true);
        setShowLoginPopup(true);
      }
    };

    if (!showAllIssues && !user) {
      window.addEventListener('scroll', handleScroll);
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAllIssues, user]);

  useEffect(() => {
    if (!showLoginPopup) {
      setIsBlurred(false);
    }
  }, [showLoginPopup]);

  const handleSignIn = () => {
    setShowAllIssues(true);
    setIsBlurred(false);
    setShowLoginPopup(false);
  };

  const handleCopy = async (text: string, id: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!analysis) return null;

  const visibleIssues = showAllIssues || user ? analysis.issues : analysis.issues.slice(0, 3);

  return (
    <div className="space-y-12">
      <AnalysisHeader />

      <ResizableContainer>
        <IssueList
          issues={visibleIssues}
          screenshot={screenshot}
          onImageClick={setSelectedImage}
          onCopy={handleCopy}
          copiedId={copiedId}
          isBlurred={isBlurred}
        />
      </ResizableContainer>

      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => setShowLoginPopup(false)}
        onSignIn={handleSignIn}
      />

      <ImagePreview 
        image={selectedImage} 
        onClose={() => setSelectedImage(null)} 
      />
    </div>
  );
}